import { keyOrDie, type Path } from "./index.js";

type Node<T> = {
    children: { [key: string]: Node<T> };
} & (
    | {
          hasValue: true;
          value: T;
      }
    | { hasValue: false }
);

function emptyNode<T>(): Node<T> {
    return { hasValue: false, children: {} };
}

function findNode<T>(root: Node<T>, path: Path): Node<T> | undefined {
    let node: Node<T> | undefined = root;
    for (const part of path) {
        node = node.children[part as string];
        if (node === undefined) {
            return undefined;
        }
    }
    return node;
}

function subtreeHasValue<T>(node: Node<T>): boolean {
    if (node.hasValue) {
        return true;
    }
    for (const key in node.children) {
        if (subtreeHasValue(node.children[key]!)) {
            return true;
        }
    }
    return false;
}

function setIn<T>(node: Node<T>, path: Path, depth: number, value: T): Node<T> {
    if (depth === path.length) {
        return { hasValue: true, value, children: node.children };
    }
    const key = String(path[depth]);
    const child = node.children[key] ?? emptyNode<T>();
    const newChild = setIn(child, path, depth + 1, value);
    return {
        ...node,
        children: { ...node.children, [key]: newChild },
    };
}

function unsetIn<T>(node: Node<T>, path: Path, depth: number): Node<T> | undefined {
    if (depth === path.length) {
        if (Object.keys(node.children).length === 0) {
            return undefined;
        }
        return { hasValue: false, children: node.children };
    }
    const key = String(path[depth]);
    const child = node.children[key];
    if (child === undefined) {
        return node;
    }
    const newChild = unsetIn(child, path, depth + 1);
    const newChildren = { ...node.children };
    if (newChild === undefined) {
        delete newChildren[key];
    } else {
        newChildren[key] = newChild;
    }
    if (!node.hasValue && Object.keys(newChildren).length === 0) {
        return undefined;
    }
    return { ...node, children: newChildren };
}

function pruneIn<T>(node: Node<T>, prefix: Path, depth: number): Node<T> | undefined {
    if (depth === prefix.length) {
        return undefined;
    }
    const key = String(prefix[depth]);
    const child = node.children[key];
    if (child === undefined) {
        return node;
    }
    const newChild = pruneIn(child, prefix, depth + 1);
    const newChildren = { ...node.children };
    if (newChild === undefined) {
        delete newChildren[key];
    } else {
        newChildren[key] = newChild;
    }
    if (!node.hasValue && Object.keys(newChildren).length === 0) {
        return undefined;
    }
    return { ...node, children: newChildren };
}

function* walk<T>(node: Node<T>, path: Path): IterableIterator<[Path, T]> {
    if (node.hasValue) {
        yield [path.slice(), node.value as T];
    }
    for (const key of Object.keys(node.children)) {
        yield* walk(keyOrDie(node.children, key), [...path, key]);
    }
}

function* children<T>(node: Node<T>, path: Path): IterableIterator<[Path, T]> {
    for (const key of Object.keys(node.children)) {
        const child = keyOrDie(node.children, key);
        if (child.hasValue) {
            yield [[...path, key], child.value];
        }
    }
}

export class PrefixTree<T> {
    private readonly root: Node<T>;

    private constructor(root: Node<T>) {
        this.root = root;
    }

    static empty<T>() {
        return new PrefixTree<T>(emptyNode<T>());
    }

    get(path: Path) {
        const node = findNode(this.root, path);
        return node !== undefined && node.hasValue ? (node.value as T) : undefined;
    }

    has(path: Path) {
        const node = findNode(this.root, path);
        return node !== undefined && node.hasValue;
    }

    hasInSubtree(prefix: Path) {
        const node = findNode(this.root, prefix);
        return node !== undefined && subtreeHasValue(node);
    }

    set(path: Path, value: T) {
        return new PrefixTree<T>(setIn(this.root, path, 0, value));
    }

    unset(path: Path) {
        const next = unsetIn(this.root, path, 0);
        return next === undefined ? PrefixTree.empty<T>() : new PrefixTree<T>(next);
    }

    pruneSubtree(prefix: Path) {
        if (prefix.length === 0) {
            return PrefixTree.empty<T>();
        }
        const next = pruneIn(this.root, prefix, 0);
        return next === undefined ? PrefixTree.empty<T>() : new PrefixTree<T>(next);
    }

    *entries() {
        yield* walk(this.root, []);
    }

    *entriesInSubtree(prefix: Path) {
        const node = findNode(this.root, prefix);
        if (node === undefined) {
            return;
        }
        yield* walk(node, prefix.slice());
    }

    *values(): IterableIterator<T> {
        for (const [, value] of this.entries()) {
            yield value;
        }
    }

    *children(path: Path) {
        const node = findNode(this.root, path);
        if (node === undefined) {
            return;
        }
        yield* children(node, path);
    }
}
